import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
  FloatingPortal,
} from '@floating-ui/react';

interface BirthdayPickerProps {
  value: string; // YYYY-MM-DD or ''
  onChange: (value: string) => void;
  disabled?: boolean;
  error?: boolean;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const getDaysInMonth = (month: number | null, year: number | null): number => {
  if (month === null) return 31;
  if (year === null) return month === 2 ? 28 : new Date(2001, month, 0).getDate();
  return new Date(year, month, 0).getDate();
};

const parseValue = (val: string): { year: number | null; month: number | null; day: number | null } => {
  if (!val || !/^\d{4}-\d{2}-\d{2}$/.test(val)) return { year: null, month: null, day: null };
  const [y, m, d] = val.split('-').map(Number);
  return { year: y, month: m, day: d };
};

const compose = (y: number, m: number, d: number): string =>
  `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

const BirthdayPicker: React.FC<BirthdayPickerProps> = ({ value, onChange, disabled, error }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [panelStep, setPanelStep] = useState<'month' | 'day' | 'year'>('month');
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [decadeStart, setDecadeStart] = useState<number>(
    Math.floor(new Date().getFullYear() / 10) * 10
  );
  const referenceRef = useRef<HTMLButtonElement>(null);

  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-start',
    strategy: 'fixed',
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
    ],
  });

  const setReferenceNode = (node: HTMLButtonElement | null) => {
    referenceRef.current = node;
    refs.setReference(node);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const referenceNode = referenceRef.current;
      const floatingNode = refs.floating.current;
      if (referenceNode?.contains(target) || floatingNode?.contains(target)) return;
      setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, refs.floating]);

  useEffect(() => {
    const { year, month, day } = parseValue(value);
    setSelectedYear(year);
    setSelectedMonth(month);
    setSelectedDay(day);
    if (year !== null) setDecadeStart(Math.floor(year / 10) * 10);
  }, [value]);

  const handleOpen = () => {
    if (disabled) return;
    setPanelStep('month');
    setIsOpen((prev) => !prev);
  };

  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1;
  const currentDay = today.getDate();

  const handleMonthSelect = (month: number) => {
    setSelectedMonth(month);
    const maxDays = getDaysInMonth(month, selectedYear);
    if (selectedDay !== null && selectedDay > maxDays) setSelectedDay(null);
    setPanelStep('day');
  };

  const handleDaySelect = (day: number) => {
    setSelectedDay(day);
    setPanelStep('year');
  };

  const handleYearSelect = (year: number) => {
    setSelectedYear(year);
    const maxDays = getDaysInMonth(selectedMonth, year);
    const finalDay = selectedDay !== null && selectedDay <= maxDays ? selectedDay : null;
    if (selectedMonth !== null && finalDay !== null) {
      onChange(compose(year, selectedMonth, finalDay));
      setIsOpen(false);
    } else {
      setSelectedDay(null);
      setPanelStep('day');
    }
  };

  const handlePrevDecade = () => {
    setDecadeStart((prev) => Math.max(prev - 10, Math.floor(1930 / 10) * 10));
  };

  const handleNextDecade = () => {
    setDecadeStart((prev) => Math.min(prev + 10, Math.floor(currentYear / 10) * 10));
  };

  const isMonthDisabled = (month: number): boolean =>
    selectedYear === currentYear && month > currentMonth;

  const isDayDisabled = (day: number): boolean =>
    selectedYear === currentYear &&
    selectedMonth === currentMonth &&
    day > currentDay;

  const decadeEnd = decadeStart + 9;
  const years: number[] = [];
  for (let y = decadeStart; y <= decadeEnd && y <= currentYear; y++) {
    if (y >= 1930) years.push(y);
  }

  const daysInCurrentMonth = getDaysInMonth(selectedMonth, selectedYear);

  const triggerLabel =
    selectedYear && selectedMonth && selectedDay
      ? `${selectedDay} ${MONTH_NAMES[selectedMonth - 1]} ${selectedYear}`
      : 'Select date of birth';

  return (
    <div className="w-full">
      <button
        ref={setReferenceNode}
        type="button"
        onClick={handleOpen}
        disabled={disabled}
        className={[
          'w-full px-3 py-2 border rounded-lg text-left flex items-center justify-between transition-colors',
          disabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'hover:bg-gray-50',
          error ? 'border-red-500' : 'border-gray-300',
          selectedYear && selectedMonth && selectedDay ? 'font-semibold text-gray-900' : 'text-gray-400',
        ].join(' ')}
      >
        <span>{triggerLabel}</span>
        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <FloatingPortal>
        {isOpen && (
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className="w-72 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-[1040]"
          >
            {panelStep === 'month' && (
              <>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 text-center">
                  Select Month
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {MONTH_NAMES.map((name, idx) => {
                    const month = idx + 1;
                    const isDisabled = isMonthDisabled(month);
                    const isSelected = month === selectedMonth;
                    return (
                      <button
                        key={month}
                        type="button"
                        disabled={isDisabled}
                        onClick={() => handleMonthSelect(month)}
                        className={[
                          'py-2 px-1 rounded text-sm font-medium transition-colors',
                          isSelected ? 'bg-accent text-white' : '',
                          isDisabled ? 'opacity-40 cursor-not-allowed text-gray-400' : '',
                          !isSelected && !isDisabled ? 'text-gray-700 hover:bg-gray-100' : '',
                        ].join(' ')}
                      >
                        {name}
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {panelStep === 'day' && (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => setPanelStep('month')}
                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                    aria-label="Back to month"
                  >
                    <ChevronLeft className="w-4 h-4 text-gray-600" />
                  </button>
                  <span className="text-sm font-semibold text-gray-700">
                    {selectedMonth ? MONTH_NAMES[selectedMonth - 1] : 'Month'}
                  </span>
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {Array.from({ length: daysInCurrentMonth }, (_, i) => i + 1).map((day) => {
                    const isDisabled = isDayDisabled(day);
                    const isSelected = day === selectedDay;
                    return (
                      <button
                        key={day}
                        type="button"
                        disabled={isDisabled}
                        onClick={() => handleDaySelect(day)}
                        className={[
                          'py-1.5 rounded text-sm font-medium transition-colors text-center',
                          isSelected ? 'bg-accent text-white' : '',
                          isDisabled ? 'opacity-40 cursor-not-allowed text-gray-400' : '',
                          !isSelected && !isDisabled ? 'text-gray-700 hover:bg-gray-100' : '',
                        ].join(' ')}
                      >
                        {day}
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {panelStep === 'year' && (
              <>
                <div className="flex items-center gap-2 mb-1">
                  <button
                    type="button"
                    onClick={() => setPanelStep('day')}
                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                    aria-label="Back to day"
                  >
                    <ChevronLeft className="w-4 h-4 text-gray-600" />
                  </button>
                  <span className="text-sm font-semibold text-gray-700">
                    {selectedMonth ? MONTH_NAMES[selectedMonth - 1] : 'Month'}{selectedDay ? ` ${selectedDay}` : ''}
                  </span>
                </div>

                <div className="flex items-center justify-between mb-4">
                  <button
                    type="button"
                    onClick={handlePrevDecade}
                    disabled={decadeStart <= Math.floor(1930 / 10) * 10}
                    className="p-1 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed rounded transition-colors"
                    aria-label="Previous decade"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <span className="font-semibold text-gray-900 text-center flex-1 text-sm">
                    {decadeStart} – {decadeEnd}
                  </span>
                  <button
                    type="button"
                    onClick={handleNextDecade}
                    disabled={decadeStart >= Math.floor(currentYear / 10) * 10}
                    className="p-1 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed rounded transition-colors"
                    aria-label="Next decade"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>

                <div className="grid grid-cols-4 gap-2">
                  {years.map((year) => (
                    <button
                      key={year}
                      type="button"
                      onClick={() => handleYearSelect(year)}
                      className={[
                        'py-2 px-1 rounded text-sm font-medium transition-colors',
                        year === selectedYear ? 'bg-accent text-white' : '',
                        year === currentYear && year !== selectedYear
                          ? 'border border-accent text-accent hover:bg-accent/10'
                          : '',
                        year !== selectedYear && year !== currentYear
                          ? 'text-gray-700 hover:bg-gray-100'
                          : '',
                      ].join(' ')}
                    >
                      {year}
                    </button>
                  ))}
                </div>

                <p className="text-xs text-gray-500 mt-4 text-center">1930 – {currentYear}</p>
              </>
            )}
          </div>
        )}
      </FloatingPortal>
    </div>
  );
};

export default BirthdayPicker;
